import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import useEntity from "../../hooks/useEntity";
import useLocalization from "../../hooks/useLocalization";
import useBrowser from "../../hooks/useBrowser";
import useUser from "../../hooks/useUser";

import Page from "./Page";
import Card from "../layout/Card";
import UserEditor from "../editors/UserEditor";
import InfoBox from "../layout/InfoBox";
import Call from "../ui/Call";

const User = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { getTitle } = useLocalization();
  const { windowHeight } = useBrowser();
  const {
    getTerritoryAssignemnts,
    territoryAssignments,
    getUser,
    user,
    getScheduleStatus,
  } = useUser();

  const { setEntityName, getEntityData, entityData } = useEntity();

  const [userId, setUserId] = useState(null);
  const [scheduleStatus, setScheduleStatus] = useState(null);

  useEffect(() => {
    setEntityName("User");

    setUserId(location.state.userId);
  }, []);

  useEffect(() => {
    if (!userId) return;

    getData();
  }, [userId]);

  const getData = async () => {
    await getUser(userId);
    await getTerritoryAssignemnts(userId);
    setScheduleStatus(await getScheduleStatus(userId));
  };

  const handleToolbarClick = (name) => {
    switch (name) {
      case "list":
        navigate("/users", {
          state: {
            filter: location.state.filter,
            pageNumber: location.state.pageNumber,
          },
        });
        break;
    }
  };

  const handleSave = async () => {
    await getUser(userId);
  };

  if (!user) return null;

  return (
    <Page
      pageName={user.name}
      includeToolbar
      items={[{ name: "list", iconName: "ListAlt" }]}
      onToolItemClick={handleToolbarClick}
    >
      <Box
        sx={{ display: "flex" }}
        gap={2}
      >
        <Card
          title={getTitle("personalInformation")}
          height={`${windowHeight - 140}px`}
          sx={{ flex: 3 }}
        >
          <UserEditor
            userId={userId}
            onSave={handleSave}
          />
        </Card>

        <Card
          title="Territory Assignments"
          sx={{ flex: 3 }}
          height={`${windowHeight - 140}px`}
        >
          <Box sx={{ padding: "8px" }}>
            {territoryAssignments.length > 0 ? (
              <Stack spacing={1}>
                {territoryAssignments.map((territory) => (
                  <Box
                    sx={{
                      padding: "6px",
                      borderRadius: "4px",
                      boxShadow: theme.shadows[4],
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      {territory.syndication
                        ? territory.syndication.name
                        : territory.project.name}
                    </Typography>
                    <Typography sx={{ fontSize: "12px" }}>
                      {territory.name}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "12px", marginTop: "10px" }}
                textAlign="center"
              >
                Not assigned to any territory
              </Typography>
            )}
          </Box>
        </Card>

        <Card
          title="Call Status"
          sx={{ flex: 3 }}
          height={`${windowHeight - 140}px`}
        >
          <Stack
            spacing={1}
            sx={{ padding: "8px" }}
          >
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                Incomplete Calls
              </Typography>
              <Box
                sx={{
                  maxHeight: `${(windowHeight - 250) / 2}px`,
                  overflow: "auto",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  padding: "6px",
                }}
              >
                {scheduleStatus &&
                Array.isArray(scheduleStatus.incompleteCalls) &&
                scheduleStatus.incompleteCalls.length > 0 ? (
                  <Stack spacing={1}>
                    {scheduleStatus.incompleteCalls.map((call) => (
                      <Call
                        call={call}
                        showDate
                        showProject
                        onRefresh={getData}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography>No incomplete calls</Typography>
                )}
              </Box>
            </Box>

            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                Future Calls
              </Typography>
              <Box
                sx={{
                  maxHeight: `${(windowHeight - 250) / 2}px`,
                  overflow: "auto",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  padding: "6px",
                }}
              >
                {scheduleStatus &&
                Array.isArray(scheduleStatus.futureCalls) &&
                scheduleStatus.futureCalls.length > 0 ? (
                  <Stack spacing={1}>
                    {scheduleStatus.futureCalls.map((call) => (
                      <Call
                        call={call}
                        showDate
                        showProject
                        onRefresh={getData}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography>No future calls</Typography>
                )}
              </Box>
            </Box>
          </Stack>
        </Card>
      </Box>
    </Page>
  );
};

export default User;
